import { useLocation, useRouter, type AnyRouter } from '@tanstack/react-router';
import { useEffect } from 'react';

import type { FileRouteTypes } from '../../routeTree.gen';

type RouteId = FileRouteTypes['id'];

type TitleContext = {
  params: Record<string, string>;
  /** Runtime detail a page supplies through `usePageTitleDetail`, e.g. the lead name. */
  detail?: string;
};

/**
 * `null` = no title of its own: layouts, redirects and index routes inherit the nearest
 * titled ancestor. Functions get the leaf match's params and the optional runtime detail.
 */
type RouteTitle = string | ((context: TitleContext) => string) | null;

const APP_NAME = 'K3X';
const SECTION = ' · ';

const settings = (page: string) => `Settings${SECTION}${page}`;
const superadmin = (page: string) => `Superadmin${SECTION}${page}`;

const capitalize = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ') : value;

/**
 * Every route id must appear here, so adding a route without deciding its title is a type error.
 * Keep titles free of runtime data except through `detail`: the static form (no detail) is what
 * Google Analytics reports as `page_title`, and names in it would fragment the reports.
 */
export const ROUTE_TITLES = {
  __root__: null,
  '/_authenticated': null,
  '/_authenticated/_app': null,
  '/_authenticated/_app/': null,

  '/auth': null,
  '/auth/signin': 'Sign in',
  '/auth/signup': 'Sign up',
  '/auth/forgot-password': 'Reset password',
  '/auth/action': 'Account',
  '/auth/access-disabled': 'Access disabled',
  '/auth/reviewer': 'Reviewer sign in',
  '/impersonate': 'Impersonate',
  '/invitation/$invitationId': 'Invitation',
  '/oauth/consent': 'Authorize app',

  '/_authenticated/_app/records': 'Records',
  '/_authenticated/_app/pipeline': 'Pipelines',
  '/_authenticated/_app/analytics': 'Analytics',
  '/_authenticated/_app/leads/$leadId': ({ detail }) => (detail ? `Lead: ${detail}` : 'Lead'),

  '/_authenticated/_app/database': 'Database',
  '/_authenticated/_app/database/': null,
  '/_authenticated/_app/database/$searchId': null,

  '/_authenticated/_app/agents': 'Agents',
  '/_authenticated/_app/agents/': null,
  '/_authenticated/_app/agents/new': `Agents${SECTION}New agent`,
  '/_authenticated/_app/agents/$agentId': 'Agent',
  '/_authenticated/_app/agents/$agentId/': null,
  '/_authenticated/_app/agents/$agentId/activity': `Agent${SECTION}Activity`,
  '/_authenticated/_app/agents/$agentId_/activity/$entityId': `Agent${SECTION}Activity`,
  '/_authenticated/_app/agents/$agentId/deliveries': `Agent${SECTION}Deliveries`,
  '/_authenticated/_app/agents/$agentId/webhook': `Agent${SECTION}Webhook`,

  '/_authenticated/_app/import': 'Imports',
  '/_authenticated/_app/import/': null,
  '/_authenticated/_app/import/new': `Imports${SECTION}New import`,
  '/_authenticated/_app/import/$importJobId': `Imports${SECTION}Review`,

  '/_authenticated/_app/settings': 'Settings',
  '/_authenticated/_app/settings/': null,
  '/_authenticated/_app/settings/profile': settings('Profile'),
  '/_authenticated/_app/settings/security': settings('Security'),
  '/_authenticated/_app/settings/membership': settings('Membership'),
  '/_authenticated/_app/settings/personal-ai-preferences': settings('AI preferences'),
  '/_authenticated/_app/settings/notifications': settings('Notifications'),
  '/_authenticated/_app/settings/connected-apps': settings('Connected apps'),
  '/_authenticated/_app/settings/authorized-apps': settings('Authorized apps'),
  '/_authenticated/_app/settings/company': settings('Company'),
  '/_authenticated/_app/settings/team': settings('Team'),
  '/_authenticated/_app/settings/custom-fields': settings('Custom fields'),
  '/_authenticated/_app/settings/ai-behavior': settings('AI behavior'),
  '/_authenticated/_app/settings/phone': settings('Phone'),
  '/_authenticated/_app/settings/roles': settings('Roles'),
  '/_authenticated/_app/settings/integrations': settings('Integrations'),
  '/_authenticated/_app/settings/billing': settings('Billing'),

  '/_authenticated/billing/return': 'Billing',

  '/_authenticated/onboarding': 'Onboarding',
  '/_authenticated/onboarding/$step': ({ params }) =>
    `Onboarding${SECTION}${capitalize(params.step ?? '')}`,
  '/_authenticated/onboarding/billing': `Onboarding${SECTION}Billing`,

  '/_authenticated/superadmin': 'Superadmin',
  '/_authenticated/superadmin/': null,
  '/_authenticated/superadmin/organizations': superadmin('Organizations'),
  '/_authenticated/superadmin/users': superadmin('Users'),
  '/_authenticated/superadmin/activity': superadmin('Activity'),
  '/_authenticated/superadmin/analytics': superadmin('Analytics'),
  '/_authenticated/superadmin/billing': superadmin('Billing'),
  '/_authenticated/superadmin/settings': superadmin('Settings'),
  '/_authenticated/superadmin/settings/': null,
  '/_authenticated/superadmin/settings/profile': superadmin('Profile'),
  '/_authenticated/superadmin/settings/security': superadmin('Security'),
} satisfies Record<RouteId, RouteTitle>;

type MatchLike = {
  routeId: string;
  fullPath?: string;
  params?: Record<string, string>;
};

/** What a page is, independent of which record it shows. */
export type PageInfo = {
  /** Title without runtime detail, e.g. `Lead | K3X` rather than `Lead: Acme | K3X`. */
  title: string;
  /** Route template, e.g. `/leads/$leadId` — never an actual id. */
  path: string;
};

const resolveTitle = (matches: readonly MatchLike[], detail?: string): string | undefined => {
  const params = matches.at(-1)?.params ?? {};

  for (let i = matches.length - 1; i >= 0; i--) {
    const spec = (ROUTE_TITLES as Record<string, RouteTitle | undefined>)[matches[i].routeId];
    if (spec == null) {
      continue;
    }

    return typeof spec === 'function' ? spec({ params, detail }) : spec;
  }

  return undefined;
};

/** `/agents/` → `/agents`; `/` stays `/`. */
const templatePath = (matches: readonly MatchLike[]): string => {
  const fullPath = matches.at(-1)?.fullPath || '/';
  return fullPath.length > 1 && fullPath.endsWith('/') ? fullPath.slice(0, -1) : fullPath;
};

export const formatDocumentTitle = (title: string | undefined) =>
  title ? `${title} | ${APP_NAME}` : APP_NAME;

export const getPageInfo = (matches: readonly MatchLike[]): PageInfo => ({
  title: formatDocumentTitle(resolveTitle(matches)),
  path: templatePath(matches),
});

// The detail is tied to the pathname it was set for, so a stale lead name can never leak onto
// the next page, whichever of the route change or the page's effect cleanup happens first.
let titleDetail: { pathname: string; value: string } | null = null;

/** Sets `document.title` for the router's current matches. Returns the page for analytics. */
export const syncDocumentTitle = (router: AnyRouter): PageInfo => {
  const { matches, location } = router.state;
  const detail = titleDetail?.pathname === location.pathname ? titleDetail.value : undefined;

  document.title = formatDocumentTitle(resolveTitle(matches, detail));

  return getPageInfo(matches);
};

/**
 * Adds runtime detail to the current page's title, e.g. `Lead: Acme | K3X`. Shown in the tab
 * only — analytics keeps the static title.
 */
export const usePageTitleDetail = (detail: string | null | undefined) => {
  const router = useRouter();
  const pathname = useLocation({ select: (location) => location.pathname });

  useEffect(() => {
    if (!detail) {
      return;
    }

    titleDetail = { pathname, value: detail };
    syncDocumentTitle(router);

    return () => {
      if (titleDetail?.pathname === pathname && titleDetail.value === detail) {
        titleDetail = null;
      }
    };
  }, [detail, pathname, router]);
};
